import { useEffect, useState } from 'react';
import {
  ELinxHistoryRequest,
  ELinxCancelRequest,
  ELinxHistoryItem,
  TransactionsHistoryRequest,
  TransactionsLists,
  FetchDocumentRequest,
  ElinxReminderResponse,
  RecipientsResponse,
  CardSettingsResponse,
  ScheduledTransactionsRequest,
  ScheduledTransactionsList,
} from 'vopay';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import axios from 'axios';
import vopayApi from '@/services/api.config';

const { api } = vopayApi;

export type ElinxItem = ELinxHistoryItem & ElinxReminderResponse & RecipientsResponse & CardSettingsResponse;

export default function useElinx({ bearer }: { bearer?: string } = {}) {
  const source = axios.CancelToken.source();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [details, setDetails] = useState<ElinxItem | null>(null);
  const [transaction, setTransaction] = useState<TransactionsLists | null>(null);
  const [scheduledTransaction, setScheduledTransaction] = useState<ScheduledTransactionsList | null>(null);
  const [documentLink, setDocumentLink] = useState('');
  const [documentName, setDocumentName] = useState('');

  useEffect(() => {
    return () => {
      source.cancel();
    };
  }, []);

  const fetchElinxDetails = async (params: ELinxHistoryRequest) => {
    try {
      setDataFetchingInfo(true);
      const res = await api.fetchELinxHistory(params, bearer, source);
      if (!res.Success) throw new Error(res.ErrorMessage);
      setDetails(res.ELinxRequests[0]);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const cancelElinx = async (params: ELinxCancelRequest) => {
    try {
      setDataFetchingInfo(true);
      const res = await api.cancelElinx(params, source);
      if (!res.Success) throw new Error(res.ErrorMessage);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const fetchTransaction = async (params: TransactionsHistoryRequest) => {
    try {
      setDataFetchingInfo(true);
      const res = await api.fetchTransactionsHistory(params, source);
      if (!res.Success) throw new Error(res.ErrorMessage);
      setTransaction(res.Transactions[0]);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const fetchScheduledTransaction = async (params: ScheduledTransactionsRequest) => {
    try {
      setDataFetchingInfo(true);
      const res = await api.fetchScheduledTransactions(params, source);
      if (!res.Success) throw new Error(res.ErrorMessage);
      setScheduledTransaction(res.ScheduledTransactions[0] ?? null);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const fetchDocument = async (params: FetchDocumentRequest) => {
    try {
      const res = await api.fetchDocument(params, undefined, source);
      if (!res.Success) throw new Error(res.ErrorMessage);
      setDocumentLink(res.Document);
      setDocumentName(res.DocumentName);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  return {
    details,
    transaction,
    scheduledTransaction,
    documentLink,
    documentName,
    fetchElinxDetails,
    cancelElinx,
    fetchTransaction,
    fetchScheduledTransaction,
    fetchDocument,
  };
}
